import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  Loader2,
  Pill,
  RefreshCw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Gestione allegati fattura (upload + PDF farmacia da deblistering).
 * All'invio SDI via FIC gli allegati vengono caricati sul documento (ZIP se >1).
 */
export default function FatturaAllegatiSection({
  fatturaId,
  headers,
  canEdit = false,
  onChanged,
}) {
  const [allegati, setAllegati] = useState([]);
  const [modificabile, setModificabile] = useState(canEdit);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncingDeb, setSyncingDeb] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!fatturaId || !headers) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture/${fatturaId}/allegati`, {
        headers,
      });
      setAllegati(res.data?.allegati || []);
      setModificabile(Boolean(res.data?.modificabile));
      if (res.data?.aggiunti_da_deblistering > 0) {
        toast.success(
          `${res.data.aggiunti_da_deblistering} allegato/i farmacia importati da deblistering`,
        );
        onChanged?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento allegati");
      setAllegati([]);
    } finally {
      setLoading(false);
    }
  }, [fatturaId, headers, onChanged]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !fatturaId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${fatturaId}/allegati`,
        form,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      toast.success(res.data?.message || "Allegato caricato");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const handleSyncDeblistering = async () => {
    if (!fatturaId) return;
    setSyncingDeb(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${fatturaId}/allegati/da-deblistering`,
        {},
        { headers },
      );
      toast.success(res.data?.message || "Sync deblistering completata");
      setAllegati(res.data?.allegati || []);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import da deblistering non riuscito");
    } finally {
      setSyncingDeb(false);
    }
  };

  const handleDownload = async (a) => {
    if (!a?.id) return;
    setBusyId(a.id);
    try {
      const res = await axios.get(
        `${API_URL}/api/fatturazione/fatture/${fatturaId}/allegati/${a.id}/download`,
        { headers, responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.file_name || "allegato";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (a) => {
    if (!a?.id || !window.confirm(`Eliminare l'allegato «${a.file_name}»?`)) return;
    setBusyId(a.id);
    try {
      await axios.delete(
        `${API_URL}/api/fatturazione/fatture/${fatturaId}/allegati/${a.id}`,
        { headers },
      );
      toast.success("Allegato eliminato");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    } finally {
      setBusyId(null);
    }
  };

  const editable = modificabile && canEdit;

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold text-slate-700 flex items-center gap-2 text-sm">
          <Paperclip className="w-4 h-4 text-slate-500" />
          Allegati
          {allegati.length > 0 && (
            <Badge variant="outline" className="text-[10px] font-normal">
              {allegati.length}
            </Badge>
          )}
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {editable && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={syncingDeb || loading}
                onClick={handleSyncDeblistering}
                title="Importa PDF fatture farmacia dalle voci deblistering"
              >
                {syncingDeb ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Pill className="w-3.5 h-3.5 mr-1" />
                )}
                Da deblistering
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={uploading || loading}
                onClick={() => inputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-1" />
                )}
                Carica
              </Button>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.zip,.doc,.docx,.xls,.xlsx"
                onChange={handleUpload}
              />
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            disabled={loading}
            onClick={load}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed">
        Come su Fatture in Cloud: allegati (es. fatture farmacia) restano sul documento e vengono
        inviati a FIC all&apos;invio SDI. Con più file viene creato un ZIP automatico.
      </p>

      {loading && allegati.length === 0 ? (
        <div className="py-4 text-center text-slate-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
          Caricamento…
        </div>
      ) : allegati.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">Nessun allegato.</p>
      ) : (
        <ul className="space-y-1.5">
          {allegati.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50/80 px-2.5 py-2 text-xs"
            >
              <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800 truncate">{a.file_name}</p>
                <p className="text-[10px] text-slate-500">
                  {formatBytes(a.file_size)}
                  {a.origine === "deblistering" && (
                    <Badge className="ml-1.5 text-[9px] bg-teal-100 text-teal-800 font-normal">
                      Deblistering
                    </Badge>
                  )}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-white"
                title="Scarica"
                disabled={busyId === a.id}
                onClick={() => handleDownload(a)}
              >
                {busyId === a.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
              </button>
              {editable && (
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-red-500 hover:bg-red-50"
                  title="Elimina"
                  disabled={busyId === a.id}
                  onClick={() => handleDelete(a)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
